// Policies tab — how docs behaves for this project, one card per area,
// rendered from the JSON schema the backend serves (an enum is a select, a
// boolean a checkbox, a list of choices a set of checkboxes, a list of free
// names a comma-separated box, an optional whole number a number box), so a
// new policy needs no new form here — only its
// strings under `policies.<area>.<key>`. Every setting shows its default
// and a "changed" mark; Save sends only what changed; Reset restores the
// area's defaults. The signing stamp settings follow, as their own section.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCcw, Save, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import { api, ep } from '../lib/api.js';
import { areaProblems, policyServerErrors } from '../lib/policyRules.js';
import { SigningTab } from './SigningTab.jsx';

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const parseList = (text) => [...new Set(text.split(',').map((x) => x.trim()).filter(Boolean))];

/** A list of free names as one comma-separated box; keeps what is being typed. */
function ListInput({ id, value, label, onChange, invalid }) {
  const [text, setText] = useState((value || []).join(', '));
  useEffect(() => {
    // follow a reset/save from outside, never the box's own half-typed text
    setText((prev) => (same(parseList(prev), value || []) ? prev : (value || []).join(', ')));
  }, [value]);
  return (
    <Input
      id={id}
      className="w-64 max-w-full"
      aria-label={label}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : undefined}
      value={text}
      onChange={(e) => { setText(e.target.value); onChange(parseList(e.target.value)); }}
    />
  );
}

function PolicyField({ area, name, schema, value, defaultValue, saved, error, onChange }) {
  const { t } = useTranslation('console');
  const key = `policies.${area}.${name}`;
  const id = `policy-${area}-${name}`;
  const choices = schema.type === 'array' ? schema.items?.enum : null;
  const list = schema.type === 'array' && !choices;
  const number = schema.type === 'integer' ? schema : (schema.anyOf || []).find((s) => s.type === 'integer');
  const valueLabel = (v) => {
    if (choices) return (v || []).map((c) => t(`${key}.options.${c}`)).join(', ') || '—';
    if (list) return (v || []).join(', ') || '—';
    if (number) return v == null ? t('policies.noLimit') : String(v);
    return schema.enum ? t(`${key}.options.${v}`) : t(v ? 'policies.on' : 'policies.off');
  };

  return (
    <div className="space-y-2" data-testid={id}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label htmlFor={id}>{t(`${key}.label`)}</Label>
        {choices ? (
          <div className="flex flex-wrap gap-3" role="group" aria-label={t(`${key}.label`)}>
            {choices.map((c) => (
              <label key={c} className="flex items-center gap-1.5 text-sm">
                <Checkbox
                  checked={(value || []).includes(c)}
                  onCheckedChange={(checked) => onChange(
                    checked === true ? choices.filter((x) => x === c || (value || []).includes(x)) : (value || []).filter((x) => x !== c),
                  )}
                />
                {t(`${key}.options.${c}`)}
              </label>
            ))}
          </div>
        ) : list ? (
          <ListInput id={id} value={value} label={t(`${key}.label`)} onChange={onChange} invalid={Boolean(error)} />
        ) : number ? (
          <Input
            id={id}
            type="number"
            className="w-32"
            min={number.minimum}
            max={number.maximum}
            step={1}
            required={!(schema.anyOf || []).some((s) => s.type === 'null')}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            value={value ?? ''}
            placeholder={t('policies.noLimit')}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        ) : schema.enum ? (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger id={id} className="w-64 max-w-full" aria-label={t(`${key}.label`)}><SelectValue /></SelectTrigger>
            <SelectContent>
              {schema.enum.map((option) => (
                <SelectItem key={option} value={option}>{valueLabel(option)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Checkbox id={id} checked={Boolean(value)} onCheckedChange={(checked) => onChange(checked === true)} />
        )}
      </div>
      <FieldError id={`${id}-error`} message={error} />
      <p className="text-xs text-muted-foreground">{t(`${key}.hint`)}</p>
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        {t('policies.default', { value: valueLabel(defaultValue) })}
        {!same(saved, defaultValue) && <Badge variant="outline" className="text-amber-700 dark:text-amber-300">{t('policies.changed')}</Badge>}
      </p>
    </div>
  );
}

function PolicyArea({ projectId, area, initial }) {
  const { t } = useTranslation('console');
  const [state, setState] = useState(initial);
  const [draft, setDraft] = useState(initial.values);
  const [busy, setBusy] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const properties = state.schema.properties || {};
  const changedKeys = Object.keys(properties).filter((k) => !same(draft[k], state.values[k]));
  const atDefaults = same(state.values, state.defaults) && same(draft, state.defaults);
  // Saved values always pass, so a problem is always about a change; the
  // server's refusals show beside the setting they name.
  const problems = areaProblems(properties, draft);
  const errors = { ...problems, ...serverErrors };
  const blocked = Object.keys(problems).length > 0;

  const apply = (res, message) => {
    if (res.success) {
      setState(res.response);
      setDraft(res.response.values);
      setServerErrors({});
      toast.success(message);
      return;
    }
    const placed = policyServerErrors(res.errorInfo);
    if (placed && Object.keys(placed).length) setServerErrors(placed);
    toast.error(res.message);
  };

  const save = async () => {
    setBusy('save');
    const data = Object.fromEntries(changedKeys.map((k) => [k, draft[k]]));
    const res = await api.put(ep.policyArea(projectId, area), { data });
    setBusy(null);
    apply(res, t('policies.saved'));
  };

  const reset = async () => {
    setBusy('reset');
    const res = await api.del(ep.policyArea(projectId, area));
    setBusy(null);
    apply(res, t('policies.resetDone'));
  };

  return (
    <section className="space-y-5 rounded-lg border bg-card p-5" data-testid={`policy-area-${area}`}>
      <div>
        <h3 className="text-base font-semibold text-foreground">{t(`policies.${area}.title`)}</h3>
        <p className="text-sm text-muted-foreground">{t(`policies.${area}.description`)}</p>
      </div>
      {Object.entries(properties).map(([name, schema]) => (
        <PolicyField
          key={name}
          area={area}
          name={name}
          schema={schema}
          value={draft[name]}
          defaultValue={state.defaults[name]}
          saved={state.values[name]}
          error={errors[name]}
          onChange={(v) => {
            setDraft((prev) => ({ ...prev, [name]: v }));
            setServerErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== name)));
          }}
        />
      ))}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={reset} disabled={busy !== null || atDefaults} className="gap-2" data-testid={`policy-reset-${area}`}>
          {busy === 'reset' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          {t('policies.reset')}
        </Button>
        <Button onClick={save} disabled={busy !== null || changedKeys.length === 0 || blocked} className="gap-2" data-testid={`policy-save-${area}`}>
          {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('policies.save')}
        </Button>
      </div>
    </section>
  );
}

function PolicyAreas({ projectId }) {
  const { t } = useTranslation('console');
  const [areas, setAreas] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(ep.policies(projectId)).then((res) => {
      if (cancelled) return;
      if (res.success) setAreas(res.response.areas);
      else setError(res.message);
    });
    return () => { cancelled = true; };
  }, [projectId]);

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!areas) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return (
    <section className="space-y-4" data-testid="policies">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <SlidersHorizontal className="h-5 w-5 text-muted-foreground" /> {t('policies.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('policies.description')}</p>
      </div>
      {Object.entries(areas).map(([area, initial]) => (
        <PolicyArea key={area} projectId={projectId} area={area} initial={initial} />
      ))}
    </section>
  );
}

export const PoliciesTab = ({ projectId, projectName }) => (
  <div className="space-y-10 max-w-3xl">
    <PolicyAreas projectId={projectId} />
    <hr />
    <SigningTab projectId={projectId} projectName={projectName} />
  </div>
);
